import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useT } from '@/lib/i18n';

const CONSENT_KEY = 'fernau-cookie-consent';

interface ConsentSettings {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
  timestamp: number;
}

const DEFAULT_SETTINGS: ConsentSettings = {
  necessary: true,
  functional: false,
  analytics: false,
  marketing: false,
  timestamp: 0,
};

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [settings, setSettings] = useState<ConsentSettings>(DEFAULT_SETTINGS);
  const t = useT();

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) {
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = useCallback((consent: ConsentSettings) => {
    const consentData = { ...consent, timestamp: Date.now() };
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consentData));
    setIsVisible(false);
  }, []);

  const acceptAll = useCallback(() => {
    saveConsent({ necessary: true, functional: true, analytics: true, marketing: true, timestamp: 0 });
  }, [saveConsent]);

  const acceptNecessary = useCallback(() => {
    saveConsent({ necessary: true, functional: false, analytics: false, marketing: false, timestamp: 0 });
  }, [saveConsent]);

  const acceptSelected = useCallback(() => {
    saveConsent(settings);
  }, [saveConsent, settings]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
        >
          <div className="max-w-4xl mx-auto">
            <div className="card-industrial p-6 md:p-8 shadow-2xl border-primary/20">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <Cookie className="w-5 h-5 text-primary" aria-hidden="true" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">{t('Cookie-Einstellungen', 'Cookie settings')}</h3>
                  <p className="text-sm text-muted-foreground">
                    {t('Ihre Privatsphäre ist uns wichtig', 'Your privacy matters to us')}
                  </p>
                </div>
              </div>

              <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                {t(
                  'Wir verwenden Cookies und ähnliche Technologien gemäß § 25 TDDDG, um Ihnen die bestmögliche Erfahrung auf unserer Website zu bieten. Technisch notwendige Cookies werden auf Grundlage unseres berechtigten Interesses gesetzt (Art. 6 Abs. 1 lit. f DSGVO i.V.m. § 25 Abs. 2 TDDDG). Für alle anderen Cookies benötigen wir Ihre Einwilligung (Art. 6 Abs. 1 lit. a DSGVO i.V.m. § 25 Abs. 1 TDDDG). Weitere Informationen finden Sie in unserer ',
                  'We use cookies and similar technologies in accordance with § 25 TDDDG to provide the best possible experience on our website. Technically necessary cookies are set on the basis of our legitimate interest (Art. 6 (1) (f) GDPR in conjunction with § 25 (2) TDDDG). For all other cookies we require your consent (Art. 6 (1) (a) GDPR in conjunction with § 25 (1) TDDDG). More information in our ',
                )}
                <LocalizedLink to="/datenschutz" className="text-primary hover:underline">
                  {t('Datenschutzerklärung', 'Privacy Policy')}
                </LocalizedLink>{' '}
                {t('und im', 'and the')}{' '}
                <LocalizedLink to="/impressum" className="text-primary hover:underline">
                  {t('Impressum', 'Legal Notice')}
                </LocalizedLink>.
              </p>

              {/* Details Section */}
              <AnimatePresence>
                {showDetails && (
                  <CookieDetails settings={settings} onSettingsChange={setSettings} t={t} />
                )}
              </AnimatePresence>

              {/* Privacy Note */}
              <div className="flex items-center gap-2 text-xs text-muted-foreground mb-6 p-3 bg-secondary/30 rounded-lg">
                <Shield className="w-4 h-4 text-primary shrink-0" aria-hidden="true" />
                <span>
                  <strong className="text-foreground">Privacy by Design:</strong>{' '}
                  {t(
                    'Diese Website lädt keine externen Schriften oder Tracking-Skripte. Alle Ressourcen werden lokal gehostet.',
                    'This site loads no external fonts or tracking scripts. All resources are hosted locally.',
                  )}
                </span>
              </div>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDetails(!showDetails)}
                  className="flex-1"
                >
                  {showDetails
                    ? t('Weniger anzeigen', 'Show less')
                    : t('Einstellungen anpassen', 'Adjust settings')}
                </Button>
                <Button
                  variant="outline"
                  onClick={acceptNecessary}
                  className="flex-1"
                >
                  {t('Nur notwendige', 'Necessary only')}
                </Button>
                {showDetails ? (
                  <Button
                    variant="hero"
                    onClick={acceptSelected}
                    className="flex-1"
                  >
                    {t('Auswahl speichern', 'Save selection')}
                  </Button>
                ) : (
                  <Button
                    variant="hero"
                    onClick={acceptAll}
                    className="flex-1"
                  >
                    {t('Alle akzeptieren', 'Accept all')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Cookie detail categories shown when user expands settings */
function CookieDetails({
  settings,
  onSettingsChange,
  t,
}: {
  settings: ConsentSettings;
  onSettingsChange: (s: ConsentSettings) => void;
  t: <T,>(de: T, en: T) => T;
}) {
  return (
    <motion.div
      initial={{ height: 0, opacity: 0 }}
      animate={{ height: 'auto', opacity: 1 }}
      exit={{ height: 0, opacity: 0 }}
      className="overflow-hidden mb-6"
    >
      <div className="space-y-4 pt-4 border-t border-border/30">
        {/* Notwendige Cookies */}
        <label className="flex items-start gap-4 p-4 rounded-lg bg-secondary/30 cursor-not-allowed">
          <input
            type="checkbox"
            checked
            disabled
            className="mt-1 w-4 h-4 rounded border-border"
            aria-label={t('Notwendige Cookies (immer aktiv)', 'Necessary cookies (always on)')}
          />
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="font-medium">{t('Notwendige Cookies', 'Necessary cookies')}</span>
              <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">
                {t('Erforderlich', 'Required')}
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {t(
                'Diese Cookies sind für die Grundfunktionen der Website erforderlich und können nicht deaktiviert werden. Rechtsgrundlage: § 25 Abs. 2 TDDDG i.V.m. Art. 6 Abs. 1 lit. f DSGVO.',
                'These cookies are required for the basic functions of the website and cannot be disabled. Legal basis: § 25 (2) TDDDG in conjunction with Art. 6 (1) (f) GDPR.',
              )}
            </p>
          </div>
        </label>

        {/* Funktionale Cookies */}
        <label className="flex items-start gap-4 p-4 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors cursor-pointer">
          <input
            type="checkbox"
            checked={settings.functional}
            onChange={(e) => onSettingsChange({ ...settings, functional: e.target.checked })}
            className="mt-1 w-4 h-4 rounded border-border accent-primary"
            aria-label={t('Funktionale Cookies', 'Functional cookies')}
          />
          <div className="flex-1">
            <span className="font-medium">{t('Funktionale Cookies', 'Functional cookies')}</span>
            <p className="text-sm text-muted-foreground mt-1">
              {t(
                'Speichern Ihre Einstellungen wie Theme-Präferenz und Cookie-Consent-Entscheidung für einen verbesserten Komfort bei zukünftigen Besuchen.',
                'Remember your preferences such as theme choice and cookie-consent decision for better comfort on return visits.',
              )}
            </p>
          </div>
        </label>

        {/* Analyse-Cookies */}
        <label className="flex items-start gap-4 p-4 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors cursor-pointer">
          <input
            type="checkbox"
            checked={settings.analytics}
            onChange={(e) => onSettingsChange({ ...settings, analytics: e.target.checked })}
            className="mt-1 w-4 h-4 rounded border-border accent-primary"
            aria-label={t('Analyse-Cookies', 'Analytics cookies')}
          />
          <div className="flex-1">
            <span className="font-medium">{t('Analyse-Cookies', 'Analytics cookies')}</span>
            <p className="text-sm text-muted-foreground mt-1">
              {t(
                'Helfen uns zu verstehen, wie Besucher unsere Website nutzen.',
                'Help us understand how visitors use the site.',
              )}{' '}
              <strong className="text-foreground">{t('Aktuell nicht verwendet.', 'Currently not in use.')}</strong>
            </p>
          </div>
        </label>

        {/* Marketing-Cookies */}
        <label className="flex items-start gap-4 p-4 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors cursor-pointer">
          <input
            type="checkbox"
            checked={settings.marketing}
            onChange={(e) => onSettingsChange({ ...settings, marketing: e.target.checked })}
            className="mt-1 w-4 h-4 rounded border-border accent-primary"
            aria-label="Marketing-Cookies"
          />
          <div className="flex-1">
            <span className="font-medium">{t('Marketing-Cookies', 'Marketing cookies')}</span>
            <p className="text-sm text-muted-foreground mt-1">
              {t(
                'Werden verwendet, um Werbung relevanter zu gestalten.',
                'Used to make advertising more relevant.',
              )}{' '}
              <strong className="text-foreground">{t('Aktuell nicht verwendet.', 'Currently not in use.')}</strong>
            </p>
          </div>
        </label>
      </div>
    </motion.div>
  );
}

/** Hook to check consent status */
export function useCookieConsent() {
  const [consent, setConsent] = useState<ConsentSettings | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (stored) {
      try {
        setConsent(JSON.parse(stored));
      } catch {
        setConsent(null);
      }
    }
  }, []);

  return consent;
}
